import { palette } from '@/theme/constants';
import { MuiButton, MuiIconButton, RtfComponent } from '@/components';
import { UseFirstRender } from '@/hooks';
import { approveMemory, deleteNotification, removeMemory, viewMemory, viewMemoryG } from '@/store/actions';
import { authSelector, currentStorySelector, notificationsSelector } from '@/store/selectors';
import { cdn_url, checkPermissions, findNotificationByMemoryId, generateThumbnailFromURL } from '@/utils';
import { Avatar, Box, Chip, keyframes, Modal, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { templatesSelector } from '@/store/selectors';
import { styles } from './styles';
import MediaSlider from './MediaSlider';
import EditMemoryDropdown from './EditMemoryDropdown';

interface ModalDetailProps {
  open: boolean;
  onClose: () => void;
  mediaContent?: any;
  method: () => void;
  isLocked?: boolean | false;
  extendedPalette?: any;
}

export const MemoryDetail = ({ extendedPalette, isLocked, open, onClose, mediaContent, method }: ModalDetailProps) => {
  const { t } = useTranslation();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [mediaSelected, setMediaSelected] = useState('home');
  const { user } = useSelector(authSelector);
  const { isAuth } = useSelector(authSelector);
  const story = useSelector(currentStorySelector);
  const { notifications } = useSelector(notificationsSelector);
  const dispatch = useDispatch();
  const router = useRouter();
  const [adminPalette, setAdminPalette] = useState({
    storyBackgroundColor: '#333333',
    textColor: '#fff',
    accentColor: '#BF5700',
  });

  const [approve, setApprove] = useState();
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  UseFirstRender(() => {
    if (mediaContent?.id) {
      setMediaSelected('home');
      dispatch(viewMemory(mediaContent?.id));
      setApprove(mediaContent?.approved);
    }
  }, [mediaContent?.id]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const [mediaAssets, setMediaAssets] = useState<any[]>([]);

  const generateAssets = async () => {
    const assets: any = [];

    if (!mediaContent || !cdn_url) {
      return;
    }

    let mainAssetUrl: string | null = null;

    if (mediaContent?.asset && mediaContent?.asset_type !== '' && mediaContent?.asset_type !== 'text') {
      try {
        mainAssetUrl = `${cdn_url}${mediaContent.asset}`;

        let type = 'image';
        if (mediaContent.asset_type.includes('video')) {
          type = 'video';
          const videoThumb = await generateThumbnailFromURL(mainAssetUrl).catch(() => null);

          assets.push({
            type,
            src: mainAssetUrl,
            thumb: videoThumb,
          });
        } else if (mediaContent.asset_type.includes('audio')) {
          type = 'audio';
          assets.push({
            type,
            src: mainAssetUrl,
          });
        } else if (mediaContent.asset_type.includes('image')) {
          type = 'image';
          assets.push({
            type,
            src: mainAssetUrl,
          });
        }
      } catch (error) {}
    }

    if (mediaContent?.memory_details?.complementaryImage?.length) {
      assets.push(
        ...mediaContent.memory_details.complementaryImage
          .filter((img: any) => {
            const imageUrl = img?.fileName ? `${cdn_url}${img.fileName}` : `${cdn_url}${img}`;
            return !(img?.isFeatured && imageUrl === mainAssetUrl);
          })
          .map((img: any) => ({
            type: 'image',
            src: img?.fileName ? `${cdn_url}${img.fileName}` : `${cdn_url}${img}`,
          })),
      );
    }

    if (mediaContent?.memory_details?.complementaryVideo?.length) {
      for (const vid of mediaContent.memory_details.complementaryVideo) {
        const videoUrl = vid?.fileName ? `${cdn_url}${vid.fileName}` : vid?.video ? `${cdn_url}${vid.video}` : null;
        const thumbnailUrl = videoUrl && (await generateThumbnailFromURL(videoUrl).catch(() => null));

        if (videoUrl) {
          assets.push({
            type: 'video',
            src: videoUrl,
            thumb: thumbnailUrl,
          });
        }
      }
    }

    if (mediaContent?.memory_details?.complementaryAudio?.length) {
      assets.push(
        ...mediaContent.memory_details.complementaryAudio.map((audio: any) => ({
          type: 'audio',
          src: audio?.fileName ? `${cdn_url}${audio.fileName}` : `${cdn_url}${audio}`,
        })),
      );
    }

    setMediaAssets(assets.filter((asset: any) => asset.src !== null));
  };

  useEffect(() => {
    if (!open) {
      setMediaAssets([]);
    }
  }, [open]);

  useEffect(() => {
    generateAssets();
  }, [mediaContent, cdn_url]);

  const Complementarytext = useMemo(() => {
    if (mediaContent?.memory_details?.complementaryText?.length) {
      return mediaContent.memory_details.complementaryText;
    } else {
      return null;
    }
  }, [mediaContent]);

  const notification = useMemo(
    () => findNotificationByMemoryId(notifications?.otherNotifications, mediaContent?.id),
    [notifications, mediaContent?.id],
  );
  UseFirstRender(() => {
    if (user && open && isAuth) dispatch(viewMemoryG(user?.id));
  }, [user, dispatch, open, isAuth]);
  const handleDeleteMemoryRequest = (notificationId: string, actions: any) => {
    const { memory_id, story_id } = actions;
    dispatch(deleteNotification(notificationId));
    if (actions?.type === 'APPROVAL' && memory_id && story_id) {
      dispatch(removeMemory({ id: memory_id, story_id }));
    }
  };

  const handleAcceptMemory = () => {
    dispatch(approveMemory({ id: mediaContent?.id, story_id: story?.id }));
    if (notification) dispatch(deleteNotification(notification.id));
    onClose();
    router.push(`/app/story/${story?.url}`);
  };

  const [height, setHeight] = useState(false);

  const boxRef = useRef<any>(null);
  useEffect(() => {
    const resize = () => {
      const windowOffset = window.innerHeight - 240;
      if (boxRef?.current?.offsetHeight > windowOffset) return setHeight(true);
      setHeight(false);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [boxRef.current, mediaSelected]);

  const { template } = useSelector(templatesSelector);

  useEffect(() => {
    if (template?.template?.colors) {
      const colors = template.template.colors.reduce((acc: any, color: any) => {
        switch (color.PLabel) {
          case 'storyBackground':
            acc.storyBackgroundColor = color.PValue;
            break;
          case 'TextColor':
            acc.textColor = color.PValue;
            break;
          case 'AccentColor':
            acc.accentColor = color.PValue;
            break;
          default:
            break;
        }
        return acc;
      }, {});

      setAdminPalette({
        storyBackgroundColor: colors.storyBackgroundColor || '#333333',
        textColor: colors.textColor || '#fff',
        accentColor: colors.accentColor || '#BF5700',
      });
    }
  }, [template]);
  const GetInitials = (firstName = '', lastName = '') => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };

  const accentColor = adminPalette.accentColor;
  const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(0);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

  const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(0);
  }
`;
  const assetContent = mediaContent?.type == 'text' && JSON.parse(mediaContent?.asset || '[]');
  const complementaryText = mediaContent?.memory_details?.complementaryText || [];

  const isTextAssetSameAsComplementary =  mediaContent?.type == 'text' && ( JSON.stringify(assetContent) === JSON.stringify(complementaryText));

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        ...styles.modal,
        animation: `${open ? fadeIn : fadeOut} 250ms ease-in-out`,
        overflowY: 'auto',
      }}>
      <Box
        display={'flex'}
        width={isMobile ? '100%' : '80%'}
        maxWidth={isMobile ? '100%' : '80%'}
        minHeight={isMobile ? '100%' : '100vh'}
        maxHeight={isMobile ? '100%' : '100vh'}
        padding={isMobile ? '0.5rem' : '1rem'}
        height={isMobile ? '100%' : 'auto'}
        borderRadius={isMobile ? '0' : '0.625rem'}
        flexDirection={'column'}
        justifyContent={'space-between'}
        position={'relative'}
        overflow={'auto'}
        bgcolor='#131544'
        sx={{ backdropFilter: 'blur(1.5625rem)', outline: 'none' }}
        border={isMobile ? 'none' : `0.063rem solid ${palette.cardBorder}`}>
        {/* Icons Row */}
        <Box>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            width='100%'
            marginBottom={isMobile ? '1.5rem' : '1.5rem'}
            marginTop={isMobile ? '15px' : ''}>
            {/* Top Row: Left Icon, Avatar, Username, Right Icon */}
            <Box position='sticky' top='0.5rem' width='100%' paddingY='1rem'>
              {/* Left Icon - absolute left */}
              <Box position='absolute' left={0} top='50%' sx={{ transform: 'translateY(-50%)' }}>
                <MuiIconButton
                  icon={isMobile ? '/icons/close' : '/icons/close'}
                  altIcon='close'
                  background='transparent'
                  method={onClose}
                />
              </Box>

              {/* Right Dropdown - absolute right (conditionally rendered) */}
              {!isLocked &&
                ((checkPermissions(user?.roles || [], 'CLIENT_MEMORY_DELETE', story?.id) &&
                  user?.id === mediaContent?.user_id) ||
                  user?.id === story?.user_id ||
                  user?.roles?.find(
                    (role: any) => role.story_id === story?.id && role.role.name === 'Story_Owner',
                  )) && (
                  <Box position='absolute' right={0} top='50%' sx={{ transform: 'translateY(-50%)' }}>
                    <EditMemoryDropdown mediaContent={mediaContent} deleteMemory={method} onClose={onClose} />
                  </Box>
                )}

              {/* Centered Content */}
            </Box>
            <div>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='center'
                gap='0.5rem'
                marginTop={isMobile ? '' : '2rem'}
                sx={{ textAlign: 'center' }}>
                {/* Avatar */}
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    src={`${cdn_url}${mediaContent?.user?.picture}`}
                    alt='Profile'
                    sx={{ width: '31px', height: '30px', fontSize: '0.8rem' }}>
                    {GetInitials(mediaContent?.user?.name, mediaContent?.user?.lastname)}
                  </Avatar>
                  {story?.user_id === mediaContent?.user_id && (
                    <Box
                      component='img'
                      src='/icons/Star.svg'
                      alt='Star'
                      sx={{ position: 'absolute', top: '-2px', right: '0px', width: 11, height: 11 }}
                    />
                  )}
                </Box>

                {/* Username */}
                <Typography fontSize='12px' color='#fff'>
                  {mediaContent?.user?.name} {mediaContent?.user?.lastname}
                </Typography>
                <Box display={'flex'} alignContent={'center'} justifyContent={'center'}>
                  {/* Pending Approval Chip */}
                  {!mediaContent?.approved && (
                    <Chip
                      label='Pending Approval'
                      size='small'
                      sx={{
                        color: '#00008B',
                        backgroundColor: '#ADD8E6',
                        borderRadius: '1.875rem',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Bottom Row: Title */}
              <Typography fontSize='1.4rem' fontWeight='bold' textAlign='center' marginTop='5px'>
                {mediaContent?.title}
              </Typography>
            </div>
            {mediaAssets.length > 0 && <MediaSlider mediaAssets={mediaAssets} Complementarytext={Complementarytext} />}
            <Box
              display='flex'
              justifyContent='flex-start'
              alignItems='flex-start'
              flexDirection='column'
              width={isMobile ? '358px' : '440px'}
              maxWidth={isMobile ? '358px' : '440px'}
              height={'100%'}>
              {mediaContent?.asset_type === 'text' && !isTextAssetSameAsComplementary && (
                <Box
                  padding={'1rem'}
                  width='100%'
                  borderRadius={'0.5rem'}
                  marginTop={'1rem'}
                  marginBottom={isMobile ? '1rem' : '1rem'}>
                  <Box 
                    height='100%'
                    sx={{
                      scrollbarWidth: 'none',
                      '-ms-overflow-style': 'none',
                      '&::-webkit-scrollbar': {
                        width: '0px',
                        height: '0px',
                        display: 'none',
                      },
                    }}>
                    <Typography
                      marginRight={'0.5rem'}
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                      }}>
                      <RtfComponent rtf={assetContent} label={'p'} />
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              display='flex'
              justifyContent='flex-start'
              alignItems='flex-start'
              flexDirection='column'
              width={isMobile ? '358px' : '440px'}
              maxWidth={isMobile ? '358px' : '440px'}
              height={'100%'}>
              {Complementarytext !== null && (
                <Box
                  width={'100%'}
                  mt={'1rem'}
                  padding={'1rem'}
                  borderRadius={'0.5rem'}
                  marginBottom={isMobile ? '0' : '1rem'}>
                  <Typography
                    marginRight={'0.5rem'}
                    sx={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}>
                    <RtfComponent rtf={Complementarytext} label={'p'} />
                  </Typography>
                </Box>
              )}
          </Box>
          </Box>
          {/* Buttons Section */}
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            width={isMobile ? '100%' : '100%'}
            sx={{ marginTop: isMobile ? '0' : '35px', paddingBottom: isMobile ? '20px' : '0' }}>
            {!approve &&
              !isLocked &&
              (checkPermissions(user?.roles || [], 'CLIENT_STORY_UPDATE', story?.id) ||
                user?.id === story?.user_id ||
                user?.roles?.find((role: any) => role.story_id === story?.id && role.role.name === 'Story_Owner')) && (
                <>
                  <Box width={'6.5rem'} marginRight={'1rem'}>
                    <MuiButton
                      type='button'
                      backgroundColor={accentColor}
                      loading={false}
                      borderRadius='1.875rem'
                      disabled={false}
                      method={() => {
                        handleAcceptMemory();
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: accentColor,
                        },
                      }}>
                      <Typography variant='button' color={palette.white}>
                        Approve
                      </Typography>
                    </MuiButton>
                  </Box>
                  <Box width={'6.5rem'} marginRight={'1rem'}>
                    <MuiButton
                      type='button'
                      backgroundColor={accentColor}
                      loading={false}
                      borderRadius='1.875rem'
                      disabled={false}
                      method={method}
                      sx={{
                        '&:hover': {
                          backgroundColor: accentColor,
                        },
                      }}>
                      <Typography variant='button' color={palette.white}>
                        Deny
                      </Typography>
                    </MuiButton>
                  </Box>
                </>
              )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
